import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {getUserFacingNoun} from 'shells/edition/get-user-facing-noun';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';
import {Title} from 'shells/typography/title';

import type {
    EditionFragment as Edition,
    EditionFragment_audience as Audience,
} from 'nutshell-graphql-types';

import {UnscheduleBanner} from './unschedule-banner';

import styles from './preview-data.module.css';

type Props = {
    edition: Edition;
    onUnschedule?: () => Promise<any>;
};

export function PreviewData(props: Props) {
    const getUtmCampaignParameters = () => {
        if (props.edition.utmParameters.campaign && props.edition.utmParameters.campaign.length) {
            return <div>utm_campaign = {props.edition.utmParameters.campaign}</div>;
        }

        return <div>utm_campaign = {props.edition.defaultUtmParameters.campaign}</div>;
    };

    return (
        <StackLegacy spacing={16}>
            {NutClientConfig.hasNewAudienceManagementNUT14721 &&
            props.edition.status === 'SCHEDULED' &&
            props.onUnschedule ? (
                <UnscheduleBanner
                    onUnschedule={props.onUnschedule}
                    userFacingNoun={getUserFacingNoun(props.edition.campaign.type)}
                />
            ) : undefined}
            {renderPreviewDetailItem(
                'From',
                props.edition.from ? props.edition.from.emailAddress.formatted : 'NO FROM ADDRESS',
                !props.edition.from
            )}
            {NutClientConfig.hasNewAudienceManagementNUT14721
                ? renderToPreviewDetailItem(props.edition.audience)
                : renderPreviewDetailItem(
                      'To',
                      props.edition.audience ? props.edition.audience.name : 'NO AUDIENCE SELECTED',
                      !props.edition.audience,
                      props.edition.audience ? props.edition.audience.htmlUrlPath : undefined
                  )}
            {props.edition.useUtmParameters
                ? renderPreviewDetailItem(
                      'UTM parameters',
                      <div>
                          {props.edition.utmParameters.source &&
                          props.edition.utmParameters.source.length ? (
                              <div>utm_source = {props.edition.utmParameters.source}</div>
                          ) : undefined}
                          {props.edition.utmParameters.medium &&
                          props.edition.utmParameters.medium.length ? (
                              <div>utm_medium = {props.edition.utmParameters.medium}</div>
                          ) : undefined}
                          {getUtmCampaignParameters()}
                      </div>,
                      !props.edition.emailTemplate ||
                          !props.edition.emailTemplate.previewText.length
                  )
                : undefined}
        </StackLegacy>
    );
}

function renderToPreviewDetailItem(audience: Audience | null): React.ReactElement {
    // If we have an audience that isn't visible and isn't from saved filter, it is a custom segment
    if (audience && audience.type !== 'VISIBLE' && !audience.fromSavedFilter) {
        return (
            <div className='flex flex-dir-col'>
                <Title>To</Title>
                <Body>Custom segment</Body>
                <div className={styles['recipient-count']}>
                    {audience.membershipStats.subscribedCount.toLocaleString()}{' '}
                    {audience.membershipStats.subscribedCount !== 1 ? 'recipients' : 'recipient'}
                </div>
            </div>
        );
    }

    // This covers audiences and saved lists
    if (audience) {
        return (
            <div className='flex flex-dir-col'>
                <Title>To</Title>
                <Link
                    variant='primary'
                    href={
                        audience.fromSavedFilter
                            ? audience.fromSavedFilter.htmlUrlPath
                            : audience.htmlUrlPathForList
                    }
                    newTab={true}
                >
                    {audience.fromSavedFilter ? audience.fromSavedFilter.name : audience.name}
                </Link>
                <div className={styles['recipient-count']}>
                    {audience.membershipStats.subscribedCount.toLocaleString()}{' '}
                    {audience.membershipStats.subscribedCount !== 1 ? 'recipients' : 'recipient'}
                </div>
            </div>
        );
    }

    return <div />;
}

/**
 * Generate a detail view for the preview. This consists of a simple title and
 * detail text. Its mostly pulled into this function so when I need to change
 * the style of these items I can do it all at once.
 *
 * @param {string} title The title of the detail item
 * @param {string} detail The detail text to show
 * @param {boolean} detailSecondaryMode If the detail text should be a lighter
 * color and italicized
 * @param {string} link If the detail info is a link, this is the destination
 */
function renderPreviewDetailItem(
    title: string,
    detail: string | React.ReactNode,
    detailSecondaryMode = false,
    link: string | null = null
): React.ReactElement {
    return (
        <div>
            <Title>{title}</Title>
            <div className={detailSecondaryMode ? styles['detail-text-secondary'] : undefined}>
                <Body>
                    {link ? (
                        // @ts-expect-error
                        <Link variant='primary' as={RouterLink} to={link}>
                            {detail}
                        </Link>
                    ) : (
                        detail
                    )}
                </Body>
            </div>
        </div>
    );
}
